import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatSuffix, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { TypeSafeMatCellDeferred } from 'src/app/directives/type-safe-mat-cell-def.directive';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { Company } from 'src/app/models/company.model';
import { Role } from 'src/app/models/role.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UtilsService } from 'src/app/services/utils.service';
import { AddUserDialogComponent } from '../users/add-user-dialog/add-user-dialog.component';
import { AddCompanyDialogComponent } from './add-company-dialog/add-company-dialog.component';
import { UserProfilePictureComponent } from 'src/app/components/utils/user-profile-picture/user-profile-picture.component';

@Component({
  selector: 'app-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    TypeSafeMatCellDeferred,
    RouterLink,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatPrefix,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgOptimizedImage,
    UserProfilePictureComponent,
  ],
})
export class CompaniesComponent extends IndexTemplateComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly destroy_ref = inject(DestroyRef);
  public readonly permissions = inject(PermissionsService);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  displayedColumns: string[] = ['name', 'active_products', 'account_manager', 'status', 'actions'];
  dataSource = new MatTableDataSource<CompanySerialized>([]);
  roles: Role[] = [];
  filterForm!: UntypedFormGroup;
  is_opening_bisual_company = false;

  override filterFormExtraParams: Record<string, unknown> = {
    scopes: [inject(ActivatedRoute).snapshot.queryParamMap.get('scopes') ?? ''],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    this.fetchRoles();
    super.ngOnInit();
    this.listenSearchFormControl();
    this.listenScopesFormControl();
  }

  editBisualCompany(): void {
    if (this.is_opening_bisual_company) {
      return;
    }

    this.is_opening_bisual_company = true;
    this.api.getBisualCompany().subscribe({
      next: company => {
        void this.router.navigate(['/employees', 'direction', 'companies', company.id]);
      },
      error: () => {
        this.is_opening_bisual_company = false;
        this.change_detector_ref.markForCheck();
      },
    });
  }

  deleteCompany(index: number): void {
    const company = this.dataSource.data[index];
    if (confirm(`¿Estás seguro que quieres eliminar la empresa "${company.fact_name}"?`)) {
      this.api.deleteCompany(company.id).subscribe(() => {
        this.fetchData();
      });
    }
  }

  addCompany(): void {
    const dialogRef = this.dialog.open(AddCompanyDialogComponent, {
      width: '750px',
    });

    dialogRef.afterClosed().subscribe((company: (Partial<Company> & { create_user?: boolean }) | null) => {
      if (company != null) {
        this.api.createCompany(company as Company).subscribe(data => {
          this.fetchData();
          if (company.create_user) {
            this.addCompanyUser(data.id);
          }
        });
      }
    });
  }

  addCompanyUser(company_id: number): void {
    this.dialog.open(AddUserDialogComponent, {
      width: '750px',
      data: {
        company_id: company_id,
      },
    });
  }

  override fetchData(): void {
    const scopes = this.filterForm.value.scopes ?? '';
    const params: Record<string, string | number> = {
      page: this.filterForm.get('page')?.value ?? 1,
      per_page: this.filterForm.get('per_page')?.value ?? this.pageSize,
      order_by: 'fact_name:asc',
      with: 'active_account_manager_members.user',
    };

    if (typeof scopes === 'string' && scopes !== '') {
      params['scopes'] = scopes;
    }

    const search = this.filterForm.value.search;
    if (typeof search === 'string' && search !== '') {
      params['search'] = search;
    }

    this.api.getCompaniesPaged(params).subscribe((data: ArrayResponse<Company>) => {
      this.dataSource.data = data.data.map(company => this.serializeCompany(company));
      this.setMetadata(+data.meta.total, data.meta.current_page - 1, data.meta.per_page);
      this.change_detector_ref.markForCheck();
    });
  }

  private fetchRoles(): void {
    this.permissions.rolesObservable.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(data => {
      if (data != null) {
        this.roles = Object.keys(data).map(k => data[k]);
        this.change_detector_ref.markForCheck();
      }
    });
  }

  private listenSearchFormControl(): void {
    this.filterForm.controls['search'].valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }

  private listenScopesFormControl(): void {
    this.filterForm
      .get('scopes')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }

  private serializeCompany(company: Company): CompanySerialized {
    const active_company_products_count = company.active_company_products_count ?? 0;
    const is_active = active_company_products_count > 0;

    return {
      id: company.id,
      fact_name: company.fact_name,
      active_company_products_count,
      is_active,
      status_label: is_active ? 'Activo' : 'Inactivo',
      account_managers: (company.active_account_manager_members ?? []).map(member => ({
        full_name: `${member.user.name} ${member.user.surnames}`.trim(),
        pokemon_profile_picture_url: member.user.pokemon_profile_picture_url ?? null,
        serious_profile_picture_url: member.user.serious_profile_picture_url ?? null,
      })),
    };
  }
}

interface CompanySerialized {
  id: number;
  fact_name: string;
  active_company_products_count: number;
  is_active: boolean;
  status_label: string;
  account_managers: CompanyAccountManagerSerialized[];
}

interface CompanyAccountManagerSerialized {
  full_name: string;
  pokemon_profile_picture_url: string | null;
  serious_profile_picture_url: string | null;
}
